import type { Container } from 'pixi.js';
import { MAP_H, MAP_W, MAX_DEPTH } from '../sim/constants';
import { ELEV_STEP, HALF_H, HALF_W } from '../render/iso';

const ZOOM_LEVELS = [1, 2, 3, 4] as const;
const WORLD_MIN_X = -MAP_H * HALF_W - 64;
const WORLD_MAX_X = MAP_W * HALF_W + 64;
const WORLD_MIN_Y = -64;
const WORLD_MAX_Y = (MAP_W + MAP_H) * HALF_H + MAX_DEPTH * ELEV_STEP + 64;
const PAN_SPEED_PX_PER_S = 700; // screen px, so pan feel is zoom-invariant

export class CameraController {
  /** World point at the center of the screen. */
  x = 0;
  y = 0;
  private zoomIndex = 1;

  get zoom(): number {
    return ZOOM_LEVELS[this.zoomIndex]!;
  }

  centerOn(worldX: number, worldY: number): void {
    this.x = worldX;
    this.y = worldY;
    this.clamp();
  }

  apply(world: Container, screenW: number, screenH: number): void {
    const zoom = this.zoom;
    world.scale.set(zoom);
    world.position.set(Math.round(screenW / 2 - this.x * zoom), Math.round(screenH / 2 - this.y * zoom));
  }

  screenToWorld(screenX: number, screenY: number, screenW: number, screenH: number): { x: number; y: number } {
    const zoom = this.zoom;
    return {
      x: this.x + (screenX - screenW / 2) / zoom,
      y: this.y + (screenY - screenH / 2) / zoom,
    };
  }

  panByScreen(dx: number, dy: number): void {
    this.x -= dx / this.zoom;
    this.y -= dy / this.zoom;
    this.clamp();
  }

  panKeys(dtSeconds: number, left: boolean, right: boolean, up: boolean, down: boolean): void {
    const step = (PAN_SPEED_PX_PER_S * dtSeconds) / this.zoom;
    if (left) this.x -= step;
    if (right) this.x += step;
    if (up) this.y -= step;
    if (down) this.y += step;
    this.clamp();
  }

  /** Integer zoom steps, anchored so the world point under the cursor stays put. */
  zoomStep(direction: 1 | -1, anchorScreenX: number, anchorScreenY: number, screenW: number, screenH: number): void {
    const nextIndex = Math.max(0, Math.min(ZOOM_LEVELS.length - 1, this.zoomIndex + direction));
    if (nextIndex === this.zoomIndex) return;
    const before = this.screenToWorld(anchorScreenX, anchorScreenY, screenW, screenH);
    this.zoomIndex = nextIndex;
    const after = this.screenToWorld(anchorScreenX, anchorScreenY, screenW, screenH);
    this.x += before.x - after.x;
    this.y += before.y - after.y;
    this.clamp();
  }

  private clamp(): void {
    this.x = Math.max(WORLD_MIN_X, Math.min(WORLD_MAX_X, this.x));
    this.y = Math.max(WORLD_MIN_Y, Math.min(WORLD_MAX_Y, this.y));
  }
}
